import { BaseProvider } from './BaseProvider';
import { ProviderType, ProviderModel, GenerateRequest, GenerateResponse, PROVIDER_DEFAULTS } from './types';

export class OpenAICompatibleProvider extends BaseProvider {
  readonly type: ProviderType = 'lmstudio';
  readonly name = 'LM Studio';
  readonly supportsStreaming = true;
  readonly supportsFunctionCalling = false;
  readonly requiresApiKey = false;

  private getBaseUrl(): string {
    return this.config?.baseUrl || PROVIDER_DEFAULTS.lmstudio.baseUrl || 'http://localhost:1234';
  }

  async testConnection(): Promise<{ success: boolean; message: string; models?: ProviderModel[] }> {
    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/v1/models`, {
        method: 'GET',
        headers: this.getHeaders()
      }, 5000);

      if (!response.ok) {
        return { success: false, message: `LM Studio server returned ${response.status}` };
      }

      const data = await response.json();
      const models = (data.data || []).map((m: any) => ({
        id: m.id,
        name: m.id,
        description: 'Local model'
      }));

      return {
        success: true,
        message: `Connected to LM Studio. ${models.length} models loaded.`,
        models
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, message: 'Connection timeout. Is LM Studio running?' };
      }
      return { success: false, message: `Cannot connect to LM Studio: ${this.formatError(error)}` };
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (this.config?.apiKey) {
      headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    }
    return headers;
  }

  async listModels(): Promise<ProviderModel[]> {
    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/v1/models`, {
        method: 'GET',
        headers: this.getHeaders()
      }, 5000);

      if (!response.ok) {
        return [{ id: 'local-model', name: 'Local Model' }];
      }

      const data = await response.json();
      return (data.data || []).map((m: any) => ({
        id: m.id,
        name: m.id,
        description: m.owned_by || 'Local model'
      }));
    } catch {
      return [{ id: 'local-model', name: 'Local Model' }];
    }
  }

  async generate(request: GenerateRequest): Promise<GenerateResponse> {
    const model = this.config?.model || 'local-model';
    
    const messages = [];
    
    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }
    
    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/v1/chat/completions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        model,
        messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens ?? 4096,
        stream: false
      })
    }, 120000);

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`LM Studio error: ${error}`);
    }

    const data = await response.json();
    const choice = data.choices?.[0];
    
    return {
      text: choice?.message?.content || '',
      model: data.model || model,
      finishReason: choice?.finish_reason || 'stop',
      usage: data.usage ? {
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
        totalTokens: data.usage.total_tokens
      } : undefined
    };
  }

  async *generateStream(request: GenerateRequest): AsyncGenerator<string, void, unknown> {
    const model = this.config?.model || 'local-model';
    
    const messages = [];
    
    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }
    
    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    const response = await fetch(`${this.getBaseUrl()}/v1/chat/completions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        model,
        messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens ?? 4096,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`LM Studio error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;
        
        try {
          const parsed = JSON.parse(data);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) {
            yield content;
          }
        } catch {
          continue;
        }
      }
    }
  }
}
